import { ColDef } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useMemo, useRef } from "react";
import { AggregateEsopReportData } from "../../../api/esopStats";
import { sideBar } from "../../../utils/AgGridConfig";

type AggregateEsopReportPreviewProps = {
  report: AggregateEsopReportData;
  onClose: () => void;
};

export default function AggregateEsopReportPreview({
  report,
  onClose,
}: AggregateEsopReportPreviewProps) {
  const gridApi = useRef<AgGridReact>(null);
  const columnDefs = useMemo<ColDef[]>(
    () =>
      report.columns.map((column) => ({
        headerName: column.label,
        field: column.key,
        minWidth: 180,
        valueFormatter: (params) =>
          typeof params.value === "number"
            ? Intl.NumberFormat("en-IN", {
                maximumFractionDigits: 2,
              }).format(params.value)
            : params.value ?? "-",
      })),
    [report.columns],
  );
  const defaultColDef = useMemo<ColDef>(
    () => ({ resizable: true, filter: true, minWidth: 150, flex: 1 }),
    [],
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex h-[85vh] w-full max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg bg-white shadow-xl sm:max-w-7xl">
        <div className="relative shrink-0 border-b border-gray-200 px-6 py-4">
          <button
            aria-label="Close ESOP report preview"
            className="absolute right-4 top-4 rounded p-1 text-xl leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
          <h2 className="pr-8 text-lg font-semibold">ESOP report preview</h2>
          <p className="mt-1 pr-8 text-sm text-gray-500">
            {report.companyCount} companies · {report.reportingPeriod.fromDate}{" "}
            to {report.reportingPeriod.toDate}
          </p>
        </div>
        <div className="ag-theme-quartz min-h-0 flex-1 p-4">
          <AgGridReact
            alwaysMultiSort
            ref={gridApi}
            defaultColDef={defaultColDef}
            columnDefs={columnDefs}
            rowData={report.rows}
            suppressRowTransform={true}
            pagination={true}
            animateRows={true}
            sideBar={sideBar}
            tooltipInteraction={true}
            tooltipShowDelay={50}
            getRowStyle={(params) => ({
              background: params.rowIndex % 2 === 0 ? "#f8f8f8" : "#ffffff",
            })}
          />
        </div>
      </div>
    </div>
  );
}
